import { useEffect, useRef } from 'react';

const LOCK_STATE_KEY = '__qubitBodyScrollLockState';

const getLockState = () => {
    if (typeof window === 'undefined') return null;

    if (!window[LOCK_STATE_KEY]) {
        window[LOCK_STATE_KEY] = {
            count: 0,
            scrollY: 0,
            styles: null,
        };
    }

    return window[LOCK_STATE_KEY];
};

export const lockBodyScroll = () => {
    if (typeof document === 'undefined') return;

    const state = getLockState();
    if (!state) return;

    const { body, documentElement } = document;

    if (state.count === 0) {
        state.scrollY = window.scrollY || window.pageYOffset || 0;
        state.styles = {
            bodyOverflow: body.style.overflow,
            bodyTouchAction: body.style.touchAction,
            bodyOverscrollBehavior: body.style.overscrollBehavior,
            htmlOverflow: documentElement.style.overflow,
            htmlOverscrollBehavior: documentElement.style.overscrollBehavior,
        };

        body.style.overflow = 'hidden';
        body.style.touchAction = 'none';
        body.style.overscrollBehavior = 'none';
        documentElement.style.overflow = 'hidden';
        documentElement.style.overscrollBehavior = 'none';
    }

    state.count += 1;
};

export const unlockBodyScroll = () => {
    if (typeof document === 'undefined') return;

    const state = getLockState();
    if (!state || state.count === 0) return;

    state.count -= 1;

    if (state.count > 0) return;

    const { body, documentElement } = document;
    const styles = state.styles || {};

    body.style.overflow = styles.bodyOverflow || '';
    body.style.touchAction = styles.bodyTouchAction || '';
    body.style.overscrollBehavior = styles.bodyOverscrollBehavior || '';
    documentElement.style.overflow = styles.htmlOverflow || '';
    documentElement.style.overscrollBehavior = styles.htmlOverscrollBehavior || '';
    state.styles = null;
};

export const useBodyScrollLock = (isLocked) => {
    useEffect(() => {
        if (!isLocked) return undefined;

        lockBodyScroll();
        return () => {
            unlockBodyScroll();
        };
    }, [isLocked]);
};

const MODAL_SELECTORS = [
    '[aria-modal="true"]',
    '[role="dialog"]',
    '[data-slot="dialog-content"]',
    '[data-slot="alert-dialog-content"]',
    '[data-slot="sheet-content"]',
    '.ReactModal__Overlay',
].join(',');

const isVisibleElement = (element) => {
    if (!element || !(element instanceof HTMLElement)) return false;
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    if (element.getAttribute('aria-hidden') === 'true') return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
};

const hasFullscreenOverlayClasses = (element) => {
    const className = typeof element.className === 'string' ? element.className : '';
    if (!className) return false;

    const isFixed = /\b(?:tw-)?fixed\b/.test(className);
    const fillsViewport = /\b(?:tw-)?inset-0\b/.test(className);
    const looksModal =
        /\b(?:tw-)?bg-black(?:\/\d+)?\b/.test(className) ||
        /\b(?:tw-)?bg-gray-\d+(?:\/\d+)?\b/.test(className) ||
        /\b(?:tw-)?backdrop-blur\b/.test(className);

    return isFixed && fillsViewport && looksModal;
};

const hasOpenModal = () => {
    if (typeof document === 'undefined') return false;

    const semanticModal = Array.from(document.querySelectorAll(MODAL_SELECTORS)).some(
        isVisibleElement
    );
    if (semanticModal) return true;

    return Array.from(document.body.querySelectorAll('*')).some(
        (element) => isVisibleElement(element) && hasFullscreenOverlayClasses(element)
    );
};

const lockedScrollValue = (value) => value === 'hidden' || value === 'clip';

export const clearStaleBodyScrollLock = () => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return false;
    if (hasOpenModal()) return false;

    const state = getLockState();
    const { body, documentElement } = document;
    let changed = false;
    const fixedTop = body.style.position === 'fixed' ? body.style.top : '';
    const restoreScrollY = fixedTop ? Math.abs(parseFloat(fixedTop)) : null;

    const clearStyle = (element, property, shouldClear = Boolean(element.style[property])) => {
        if (!shouldClear) return;
        element.style[property] = '';
        changed = true;
    };

    clearStyle(body, 'overflow', lockedScrollValue(body.style.overflow));
    clearStyle(body, 'touchAction', body.style.touchAction === 'none');
    clearStyle(body, 'overscrollBehavior', body.style.overscrollBehavior === 'none');
    clearStyle(documentElement, 'overflow', lockedScrollValue(documentElement.style.overflow));
    clearStyle(
        documentElement,
        'overscrollBehavior',
        documentElement.style.overscrollBehavior === 'none'
    );

    if (body.style.position === 'fixed') {
        clearStyle(body, 'position');
        clearStyle(body, 'top');
        clearStyle(body, 'left');
        clearStyle(body, 'right');
        clearStyle(body, 'width');
    }

    if (state) {
        state.count = 0;
        state.scrollY = 0;
        state.styles = null;
    }

    if (Number.isFinite(restoreScrollY) && restoreScrollY > 0) {
        window.scrollTo(0, restoreScrollY);
    }

    return changed;
};

export const useStaleBodyScrollUnlock = () => {
    useEffect(() => {
        if (
            typeof document === 'undefined' ||
            typeof window === 'undefined' ||
            typeof MutationObserver === 'undefined'
        ) {
            return undefined;
        }

        let frame = null;
        let timeout = null;

        const run = () => {
            frame = null;
            timeout = null;
            clearStaleBodyScrollLock();
        };

        const schedule = () => {
            if (frame !== null || timeout !== null) return;
            frame = window.requestAnimationFrame(() => {
                timeout = window.setTimeout(run, 0);
            });
        };

        const observer = new MutationObserver(schedule);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'style', 'aria-hidden', 'aria-modal', 'role', 'data-state'],
        });
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class', 'style'],
        });

        schedule();
        window.addEventListener('focus', schedule);

        return () => {
            observer.disconnect();
            window.removeEventListener('focus', schedule);
            if (frame !== null) {
                window.cancelAnimationFrame(frame);
            }
            if (timeout !== null) {
                window.clearTimeout(timeout);
            }
        };
    }, []);
};

export const useGlobalModalScrollLock = () => {
    const lockedRef = useRef(false);

    useEffect(() => {
        if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
            return undefined;
        }

        let frame = null;

        const syncLockState = () => {
            frame = null;
            const shouldLock = hasOpenModal();

            if (shouldLock && !lockedRef.current) {
                lockBodyScroll();
                lockedRef.current = true;
                return;
            }

            if (!shouldLock && lockedRef.current) {
                unlockBodyScroll();
                lockedRef.current = false;
            }
        };

        const scheduleSync = () => {
            if (frame !== null) return;
            frame = window.requestAnimationFrame(syncLockState);
        };

        const observer = new MutationObserver(scheduleSync);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'style', 'aria-hidden', 'aria-modal', 'role', 'data-state'],
        });

        scheduleSync();

        return () => {
            observer.disconnect();
            if (frame !== null) {
                window.cancelAnimationFrame(frame);
            }
            if (lockedRef.current) {
                unlockBodyScroll();
                lockedRef.current = false;
            }
        };
    }, []);
};

export default useBodyScrollLock;
